import { ContainerKind } from '@/constants/ds-attendance.constants'
import type {
  DSAttendanceContainerFilterParams,
  ListContainersByCompanyParams
} from '@/types/ds-attendance.types'

/**
 * Helpers PUROS do modal `ExportDSAttendances`: recebem o recorte da fila e
 * devolvem o que vai ao arquivo (ou por que nada vai). Vivem fora do SFC para
 * serem testados sem montar o modal — e para o `.vue` não ser a única fonte de
 * um tipo que um `.ts` precise importar (isso quebra o build).
 */

/** As duas abas do modal: a exportação de registros e a de anexos (Registro de Atividade). */
export type ExportModalMode = 'records' | 'attachments'

/**
 * As chaves de `ListContainersByCompanyParams` que são da LISTAGEM, não do
 * recorte — exatamente as que `DSAttendanceContainerFilterParams` omite. O
 * `Record` exige todas e recusa qualquer outra: mudar o `Omit` lá sem mexer
 * aqui é erro de compilação, e um filtro novo na fila entra no arquivo por
 * construção, sem ninguém precisar lembrar dele.
 */
export type ListOnlyKey = Exclude<
  keyof ListContainersByCompanyParams,
  keyof DSAttendanceContainerFilterParams
>

export const LIST_ONLY_KEYS: Record<ListOnlyKey, true> = {
  page: true,
  limit: true,
  orderBy: true,
  startDate: true,
  endDate: true,
  companyIds: true,
  expectedSubmissionAtStart: true,
  expectedSubmissionAtEnd: true
}

export const isListOnlyKey = (key: string): key is ListOnlyKey => Object.hasOwn(LIST_ONLY_KEYS, key)

/**
 * `undefined` e array vazio não são filtro — só confundiriam quem inspeciona o
 * parâmetro. `false` e `null` FICAM: `onlyRequests: false` é o valor real da
 * fila, e é o que o backend lê.
 */
export const isFilterValue = (value: unknown): boolean =>
  value !== undefined && !(Array.isArray(value) && value.length === 0)

/** O recorte da fila menos o que é próprio da listagem. */
export function toExportCut(
  queueFilters: ListContainersByCompanyParams
): DSAttendanceContainerFilterParams {
  const entries = Object.entries(queueFilters).filter(
    ([key, value]) => !isListOnlyKey(key) && isFilterValue(value)
  )
  return Object.fromEntries(entries) as DSAttendanceContainerFilterParams
}

export const BLOCK_REASON = {
  /** Solicitação é cobrança sem registro: o backend devolve recorte vazio por construção. */
  ONLY_REQUESTS:
    'A aba Solicitações não gera arquivo: uma solicitação ainda não tem registro nem anexo. Volte para outra aba da fila para exportar.',
  /** `kind=ATIVIDADE` na rota de registros é 400 `ACTIVITY_EXPORT_REQUIRES_EXTRACTING`. */
  ACTIVITY_WITH_TAB:
    'Registros de Atividade não saem como arquivo de registro. Use a aba "Registro de Atividade" acima para baixar os anexos.',
  ACTIVITY_WITHOUT_TAB:
    'Registros de Atividade não saem como arquivo de registro: os anexos saem pela exportação de Registro de Atividade, que exige a permissão de extração.'
} as const

export type BlockReason = (typeof BLOCK_REASON)[keyof typeof BLOCK_REASON]

/**
 * Por que a exportação está travada ANTES do clique — ou `null`. Solicitação não
 * tem arquivo em nenhuma aba; `kind=ATIVIDADE` só trava a aba de registros, e a
 * saída apontada depende de a aba de anexos estar disponível.
 */
export function resolveBlockReason(
  queueFilters: ListContainersByCompanyParams,
  mode: ExportModalMode,
  hasAttachmentsTab: boolean
): BlockReason | null {
  if (queueFilters.onlyRequests === true) return BLOCK_REASON.ONLY_REQUESTS
  if (queueFilters.kind === ContainerKind.ATIVIDADE && mode === 'records') {
    return hasAttachmentsTab ? BLOCK_REASON.ACTIVITY_WITH_TAB : BLOCK_REASON.ACTIVITY_WITHOUT_TAB
  }
  return null
}
